/**
 * Typed localStorage + user-shape accessors.
 *
 * Per STORAGE_SPA_UPGRADE_STUDY.md §5: a single typed surface for the
 * `localStorage.getItem('user')` / `localStorage.setItem('flow', ...)`
 * call sites scattered across the SPA, plus typed read-helpers for the
 * partner-side fields (`partnerId`, `canControlLogistics`) that the JWT
 * carries but the legacy `User` interface does not declare.
 *
 * Why:
 *   - Key strings are typo-prone. `localStorage.getItem('users')` returns
 *     `null` silently; the typed accessor would not compile.
 *   - JSON parsing is duplicated everywhere (`storedUser && JSON.parse(...)`).
 *   - The partner-self-configure work needs a single chokepoint to read
 *     `partnerId` / `canControlLogistics` off the user object — Track B
 *     and `RoutePlanTab.tsx` both inline the same `as unknown as { ... }`
 *     cast today; this file is the canonical landing spot.
 *
 * Scope:
 *   - This file is additive. Existing call sites still work; they are
 *     migrated incrementally.
 *   - Not all keys live here yet. The booking flow's per-route state
 *     (`payment-state`, `klarna-payment-state`, `collection-delivery-state`,
 *     etc.) and the dashboard cache stay on raw localStorage for now —
 *     they have their own state shapes and lifecycle and would require a
 *     bigger refactor than fits this PR.
 */

import type { User } from '../contexts/UserContext';
import type { FlowType } from '../utilities/useFlow';

const KEYS = {
  user: 'user',
  flow: 'flow',
  lastRequestId: 'last_request_id',
  lastFailedRequestId: 'last_failed_request_id',
} as const;

// ── User / JWT ───────────────────────────────────────────────

export function getUser(): User | null {
  const raw = localStorage.getItem(KEYS.user);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export function setUser(user: User): void {
  localStorage.setItem(KEYS.user, JSON.stringify(user));
}

export function clearUser(): void {
  localStorage.removeItem(KEYS.user);
}

/**
 * Returns the JWT token off the stored user, or null. Convenience wrapper
 * around `getUser()?.token` for the common case where a caller wants only
 * the bearer token (e.g. setting axios defaults).
 */
export function getJWT(): string | null {
  return getUser()?.token ?? null;
}

/**
 * Mutate the stored user with an updater function. No-op if no user is
 * stored. Mirrors the legacy `updateStoredUser` pattern in
 * `services/networkRequest.ts` so the 401-token-clear and JWT-refresh
 * paths can share a single chokepoint.
 */
export function updateUser(updater: (current: User) => User): void {
  const current = getUser();
  if (!current) return;
  setUser(updater(current));
}

// ── Flow (brand) ─────────────────────────────────────────────

export function getStoredFlow(): FlowType | null {
  const raw = localStorage.getItem(KEYS.flow);
  if (raw === 'Keepr' || raw === 'Kit Keeper' || raw === 'St Annes') {
    return raw;
  }
  return null;
}

export function setStoredFlow(flow: FlowType): void {
  localStorage.setItem(KEYS.flow, flow);
}

// ── Request IDs (observability) ──────────────────────────────
//
// `last_request_id` (success) and `last_failed_request_id` (failure) are
// captured by the axios response interceptor and surfaced by the error
// boundary + AdminNotice "Reference: …" line. Per ERROR_HANDLING_PLAN.md
// §2.4, the request id is the single correlation handle ops use to find
// the matching backend log entry.

export function setLastRequestId(id: string): void {
  localStorage.setItem(KEYS.lastRequestId, id);
}

export function getLastFailedRequestId(): string | null {
  return localStorage.getItem(KEYS.lastFailedRequestId);
}

export function setLastFailedRequestId(id: string): void {
  localStorage.setItem(KEYS.lastFailedRequestId, id);
}

export function clearLastFailedRequestId(): void {
  localStorage.removeItem(KEYS.lastFailedRequestId);
}

// ── Partner-side user-shape extensions ────────────────────────
//
// The legacy `User` interface in `contexts/UserContext.ts` does not declare
// `partnerId` or `canControlLogistics`, but the JWT issued by storage-api
// for `PARTNER_MASTER` / `PARTNER_DRIVER` / `PARTNER_WAREHOUSE` principals
// carries both. We read them via a defensive cast so the legacy shape
// stays unchanged (no breaking change to admin/customer code paths).

interface PartnerUserExtensions {
  partnerId?: number | null;
  canControlLogistics?: boolean;
}

type UserWithPartnerExtensions = User & PartnerUserExtensions;

/**
 * Returns the user's role string, or null if not signed in.
 *
 * Useful for callers that want to switch on role without importing the
 * `Role` enum; the enum only declares Admin/Warehouse, but the JWT can
 * also carry CUSTOMER, PARTNER_MASTER, PARTNER_DRIVER, PARTNER_WAREHOUSE,
 * MASTER (legacy), etc.
 */
export function getRole(user: User | null | undefined): string | null {
  return user?.role ?? null;
}

/**
 * Returns the partnerId from the user, or null if the user is not a
 * partner principal or the field is missing. Defensive: handles both
 * the absent-on-legacy-shape case and the explicit `null` case from
 * backend responses.
 */
export function getPartnerIdFromUser(
  user: User | null | undefined,
): number | null {
  if (!user) return null;
  const extended = user as UserWithPartnerExtensions;
  const raw = extended.partnerId;
  if (raw == null) return null;
  // partnerId comes from the JWT as a number. Guard against a stringified
  // value sneaking in via a legacy login response.
  const num = typeof raw === 'string' ? Number(raw) : raw;
  return Number.isFinite(num) ? (num as number) : null;
}

/**
 * Returns whether the user has the `canControlLogistics` capability.
 * Defaults to `false` if the field is missing — matches the backend
 * default for drivers/warehouse staff. Master/Admin callers should
 * combine this with a role check via `getRole`.
 */
export function getCanControlLogistics(
  user: User | null | undefined,
): boolean {
  if (!user) return false;
  const extended = user as UserWithPartnerExtensions;
  return Boolean(extended.canControlLogistics);
}
